import { HelpTooltip } from '../common/HelpTooltip';
import { HeroProgressBar } from '../common/HeroProgressBar';
import { CycleHeroShell } from '../common/CycleHeroShell';
import pt from '../../locales/pt';
import { cycleDaysRemaining } from '../../utils/cycleDays';
import { formatSelfiePeriodLabel } from '../../utils/selfiePeriod';
import { formatEUR } from '../../utils/money';
import {
  aggregateReceivedBySource,
  computePlannedIncome,
  splitFixedVariableTotals,
} from '../../lib/incomeCycleUtils';

/**
 * `.pen` → `ds/card/cycle-spend-hero--income` (`YrTwN`): mesma anatomia do hero
 * de Despesas — um cartão em gradiente com `cycle-nav`, `head`, `bar-block` e
 * `foot`, `gap $space-5`.
 *
 * O `foot` de Rendimentos são duas colunas de estatística (`GARANTIDO` /
 * `INCERTO`) em `$type-section` + `$type-display-stat`, ambas sobre o
 * gradiente (`$on-hero`) — não são cartões `stat-tile` autónomos — e não há
 * `link-row`.
 *
 * A barra mede **% recebido** (`received / planned`), ao contrário de Despesas
 * que mede o que sobra do orçamento.
 */
export function IncomeCycleHero({
  activeCycle,
  cycles = [],
  cycleIndex = 0,
  onCycleIndexChange,
  summary,
  sources = [],
  isLoading = false,
  isError = false,
  onRetry,
  className,
}) {
  const received = Number(summary?.income?.total || 0);
  const planned = computePlannedIncome(sources);
  const hasPlanned = planned > 0;
  /** Progresso face ao alvo planeado do ciclo. */
  const fillPct = hasPlanned ? Math.min(100, Math.max(0, (received / planned) * 100)) : 0;

  const daysLeft = activeCycle ? cycleDaysRemaining(activeCycle) : null;

  const bySource = aggregateReceivedBySource(
    summary?.income?.by_source || [],
    sources,
    received,
  );
  const { fixed, variable } = splitFixedVariableTotals(bySource);

  const periodLabel =
    activeCycle?.start && activeCycle?.end
      ? formatSelfiePeriodLabel(activeCycle.start, activeCycle.end)
      : '';

  const daysLabel =
    daysLeft === 1
      ? pt.income.hero.dayLeft
      : daysLeft != null
        ? pt.income.hero.daysLeft.replace('{days}', String(daysLeft))
        : null;

  return (
    <CycleHeroShell
      cycles={cycles}
      cycleIndex={cycleIndex}
      onCycleIndexChange={onCycleIndexChange}
      chipLabel={pt.income.hero.cycleChip.replace('{period}', periodLabel)}
      prevAriaLabel={pt.income.cycle.prev}
      nextAriaLabel={pt.income.cycle.next}
      isLoading={isLoading}
      isError={isError}
      onRetry={onRetry}
      className={className}
      /** `.pen` › `foot` › `stat-tiles`: só existe quando já entrou dinheiro. */
      footer={
        received > 0 ? (
          <div className="flex min-w-0 gap-space-4">
            <div className="flex min-w-0 flex-1 flex-col gap-space-1">
              <p className="type-section flex min-w-0 items-start gap-space-1 text-sage-50/80">
                <span className="min-w-0 flex-1">{pt.income.hero.guaranteed}</span>
                <HelpTooltip
                  appearance="plain"
                  label={pt.income.hero.guaranteed}
                  content={pt.income.hero.guaranteedTooltip}
                />
              </p>
              <p className="type-display-stat text-sage-50">{formatEUR(fixed)}</p>
            </div>

            <div className="flex min-w-0 flex-1 flex-col gap-space-1">
              <p className="type-section flex min-w-0 items-start gap-space-1 text-sage-50/80">
                <span className="min-w-0 flex-1">{pt.income.hero.uncertain}</span>
                <HelpTooltip
                  appearance="plain"
                  label={pt.income.hero.uncertain}
                  content={pt.income.hero.uncertainTooltip}
                />
              </p>
              <p className="type-display-stat text-sage-50">{formatEUR(variable)}</p>
            </div>
          </div>
        ) : null
      }
    >
      {/* `.pen` › `head` */}
      <div className="flex min-w-0 flex-col gap-space-2">
        <div className="flex min-w-0 items-center gap-space-2 text-sage-50/80">
          <span className="type-body-sm min-w-0 flex-1">{pt.income.hero.receivedLabel}</span>
          <HelpTooltip
            appearance="plain"
            label={pt.common.cycleTooltip}
            content={pt.common.cycleTooltip}
          />
        </div>

        <p className="type-display-hero text-sage-50">{formatEUR(received)}</p>

        {hasPlanned ? (
          <p className="type-body-sm text-sage-50/80">
            {pt.income.hero.plannedLine.replace('{planned}', formatEUR(planned))}
          </p>
        ) : null}
      </div>

      {/* `.pen` › `bar-block` */}
      {hasPlanned ? (
        <div className="flex min-w-0 flex-col gap-space-2">
          <HeroProgressBar
            variant="filled"
            percentage={fillPct}
            ariaLabel={pt.income.hero.receivedLabel}
          />
          <div className="flex items-center justify-between gap-space-2 text-sage-50/80">
            <span className="type-label-meta">
              {pt.income.hero.receivedPct.replace('{pct}', String(Math.round(fillPct)))}
            </span>
            {daysLabel ? <span className="type-label-meta">{daysLabel}</span> : null}
          </div>
        </div>
      ) : daysLabel ? (
        <p className="type-label-meta text-sage-50/80">{daysLabel}</p>
      ) : null}
    </CycleHeroShell>
  );
}

export default IncomeCycleHero;
